'use client';

import {
  Toaster as ChakraToaster,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster,
} from '@chakra-ui/react';

/**
 * @example
 * ```tsx
 * import { toaster } from '@/components';
 *
 * const handleSubmit = () => {
 *   toaster.create({
 *     description: 'Submitted',
 *     type: 'success',
 *   });
 * };
 * ```
 */
export const toaster = createToaster({
  placement: 'bottom',
  pauseOnPageIdle: true,
  offsets: {
    top: '1rem',
    left: '1rem',
    right: '1rem',
    bottom: 'calc(env(safe-area-inset-bottom) + 1rem)',
  },
});

/**
 * Renders a Portal for toasts.
 *
 * https://chakra-ui.com/docs/components/toast
 *
 * For usage in components, @see {@link toaster}
 */
export const Toaster = () => {
  return (
    <Portal>
      <ChakraToaster toaster={toaster} insetInline={{ mdDown: '4' }}>
        {(toast) => (
          <Toast.Root width={{ md: 'sm' }} data-testid="Toast">
            {toast.type === 'loading' ? (
              <Spinner size="sm" color="blue.solid" />
            ) : (
              <Toast.Indicator />
            )}
            <Stack gap="1" flex="1" maxWidth="100%">
              {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
              {toast.description && (
                <Toast.Description>{toast.description}</Toast.Description>
              )}
            </Stack>
            {toast.action && (
              <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
            )}
            {toast.closable && <Toast.CloseTrigger />}
          </Toast.Root>
        )}
      </ChakraToaster>
    </Portal>
  );
};
